import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtCurrency } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import MobileHeader from "../../components/MobileHeader";
import { Slider } from "../../components/ui/slider";
import { Button } from "../../components/ui/button";
import { CheckCircle2, Repeat, Gauge, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";

const META = {
    NanoCredit: { color: "#00D084", title: "NanoCredit™", min: 1000, max: 50000, tenures: [7, 14, 30] },
    FlexCredit: { color: "#0066FF", title: "FlexCredit™", min: 5000, max: 250000, tenures: [30, 60, 90] },
    SalaryAdvance: { color: "#FF6B00", title: "SalaryAdvance™", min: 5000, max: 500000, tenures: [30] },
    SplitPay: { color: "#7000FF", title: "SplitPay™", min: 1000, max: 100000, tenures: [30, 60, 90, 120, 150, 180] },
    PrimeCredit: { color: "#FF0055", title: "PrimeCredit™", min: 50000, max: 5000000, tenures: [90, 180, 365] },
};

export default function NanoCreditApply() {
    const { product } = useParams();
    const nav = useNavigate();
    const { t } = useI18n();
    const meta = META[product] || META.NanoCredit;

    const [step, setStep] = useState(1);
    const [amount, setAmount] = useState(Math.min(30000, meta.max));
    const [tenure, setTenure] = useState(meta.tenures[Math.min(1, meta.tenures.length - 1)]);
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);
    const [approved, setApproved] = useState(null);

    useEffect(() => {
        // Auto-fetch quote
        api.post("/loans/quote", { product, amount, tenure_days: tenure })
            .then((r) => setQuote(r.data))
            .catch(() => setQuote(null));
    }, [amount, tenure, product]);

    const submit = async () => {
        setBusy(true);
        try {
            const r = await api.post("/loans/apply", { product, amount, tenure_days: tenure });
            setApproved(r.data);
            setStep(3);
            toast.success("Loan approved & disbursed!");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Application failed");
        } finally {
            setBusy(false);
        }
    };

    if (approved) {
        return <ApprovedScreen loan={approved.loan} color={meta.color} title={meta.title} t={t} onDone={() => nav("/consumer/credit/loans/" + approved.loan.id)} />;
    }

    return (
        <div data-testid="loan-apply-page">
            <MobileHeader title={meta.title} subtitle={t("loan.step_of", { n: step, total: 2 })} backTo="/consumer/credit" />

            <div className="px-5 -mt-6 pb-6 space-y-4">
                {step === 1 && (
                    <>
                        <div className="bg-white rounded-2xl border border-slate-200 p-6 product-shadow">
                            <div className="text-sm font-semibold text-slate-700">{t("loan.how_much")}</div>
                            <div className="text-center mt-6">
                                <div className="font-display text-4xl font-bold" style={{ color: meta.color }} data-testid="loan-amount-display">
                                    {fmtCurrency(amount)}
                                </div>
                            </div>
                            <Slider
                                min={meta.min}
                                max={meta.max}
                                step={Math.max(500, Math.round((meta.max - meta.min) / 100))}
                                value={[amount]}
                                onValueChange={(v) => setAmount(v[0])}
                                className="mt-6"
                                data-testid="loan-amount-slider"
                            />
                            <div className="flex justify-between text-xs text-slate-400 mt-3">
                                <span>{fmtCurrency(meta.min)}</span>
                                <span>{fmtCurrency(meta.max)}</span>
                            </div>

                            <div className="mt-7">
                                <div className="text-sm font-semibold text-slate-700 mb-3">{t("loan.select_tenure")}</div>
                                <div className="grid grid-cols-3 gap-2">
                                    {meta.tenures.map((tn) => (
                                        <button
                                            key={tn}
                                            onClick={() => setTenure(tn)}
                                            data-testid={`tenure-${tn}`}
                                            className={`py-3 rounded-xl border-2 text-sm font-semibold tap-feedback transition ${
                                                tenure === tn
                                                    ? "border-current text-white"
                                                    : "border-slate-200 text-slate-600"
                                            }`}
                                            style={tenure === tn ? { background: meta.color, borderColor: meta.color } : {}}
                                        >
                                            {tn} days
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {quote && (
                            <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid="quote-card">
                                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{t("loan.repayment")}</div>
                                <div className="font-display text-2xl font-bold text-[#0A1930] mt-1">
                                    {fmtCurrency(quote.total_repayment)}
                                </div>
                                <div className="text-xs text-slate-500">{t("loan.due_in", { n: tenure })}</div>
                                <div className="border-t border-slate-100 mt-4 pt-3 space-y-2">
                                    <Row label={t("loan.principal")} value={fmtCurrency(quote.amount)} />
                                    <Row label={t("loan.interest")} value={fmtCurrency(quote.interest)} />
                                    <Row label={t("loan.fees")} value={fmtCurrency(quote.fees)} />
                                </div>
                            </div>
                        )}

                        <Button
                            className="w-full h-12 rounded-xl font-semibold text-base"
                            style={{ background: meta.color }}
                            onClick={() => setStep(2)}
                            data-testid="loan-continue-btn"
                        >
                            {t("common.continue")}
                        </Button>
                        <div className="text-center text-xs text-slate-500 flex items-center justify-center gap-1">
                            <Zap size={12} /> {t("loan.fast_disburse")}
                        </div>
                    </>
                )}

                {step === 2 && quote && (
                    <>
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                            <h2 className="font-display font-semibold text-lg text-[#0A1930]">{t("loan.confirm_title")}</h2>
                            <div className="mt-4 space-y-2.5">
                                <Row label={t("loan.product")} value={meta.title} />
                                <Row label={t("loan.amount")} value={fmtCurrency(quote.amount)} />
                                <Row label={t("loan.tenure")} value={`${tenure} days`} />
                                <Row label={t("loan.interest")} value={fmtCurrency(quote.interest)} />
                                <Row label={t("loan.fees")} value={fmtCurrency(quote.fees)} />
                                <div className="border-t border-slate-100 pt-2.5 mt-2.5">
                                    <Row label={t("loan.total_repayment")} value={fmtCurrency(quote.total_repayment)} bold />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 p-5">
                            <div className="text-sm font-semibold text-slate-700 mb-3">{t("loan.improve_score")}</div>
                            <Tip icon={CheckCircle2} title={t("loan.repay_on_time_title")} subtitle={t("loan.repay_on_time_sub")} color="#00D084" />
                            <Tip icon={ShieldCheck} title={t("loan.use_responsibly_title")} subtitle={t("loan.use_responsibly_sub")} color="#0066FF" />
                            <Tip icon={Gauge} title={t("loan.grow_activity_title")} subtitle={t("loan.grow_activity_sub")} color="#7000FF" />
                        </div>

                        <Button
                            className="w-full h-12 rounded-xl font-semibold text-base"
                            style={{ background: meta.color }}
                            onClick={submit}
                            disabled={busy}
                            data-testid="loan-submit-btn"
                        >
                            {busy ? t("loan.approving") : t("loan.confirm_receive")}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex justify-between text-sm">
            <span className="text-slate-500">{label}</span>
            <span className={bold ? "font-display text-base font-bold text-[#0A1930]" : "font-semibold text-[#0A1930]"}>
                {value}
            </span>
        </div>
    );
}

function Tip({ icon: Icon, title, subtitle, color }) {
    return (
        <div className="flex items-start gap-3 py-2.5">
            <div className="size-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15`, color }}>
                <Icon size={18} />
            </div>
            <div>
                <div className="font-semibold text-sm text-[#0A1930]">{title}</div>
                <div className="text-xs text-slate-500">{subtitle}</div>
            </div>
        </div>
    );
}

function ApprovedScreen({ loan, color, title, t, onDone }) {
    return (
        <div className="min-h-[100dvh] flex flex-col" style={{ background: color }} data-testid="loan-approved-screen">
            <div className="flex-1 flex flex-col items-center justify-center text-white px-6 text-center">
                <div className="size-20 rounded-full bg-white/20 backdrop-blur flex items-center justify-center animate-scale-in">
                    <CheckCircle2 size={48} strokeWidth={2.5} />
                </div>
                <h1 className="font-display text-3xl font-bold mt-6">{t("loan.approved")}</h1>
                <p className="text-white/90 mt-1">{t("loan.approved_subtitle")}</p>

                <div className="mt-8 bg-white/15 backdrop-blur rounded-3xl p-6 w-full max-w-xs">
                    <div className="font-display text-4xl font-bold">{fmtCurrency(loan.amount)}</div>
                    <div className="text-xs text-white/80 mt-1">{t("loan.disbursed_to_wallet")}</div>
                </div>

                <div className="mt-6 bg-white/15 backdrop-blur rounded-2xl p-4 w-full max-w-xs space-y-2 text-sm text-left">
                    <div className="text-xs uppercase tracking-wider text-white/70 font-bold mb-1">{t("loan.repayment_details")}</div>
                    <div className="flex justify-between"><span className="text-white/80">{t("loan.due_date")}</span><span className="font-semibold">{new Date(loan.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span></div>
                    <div className="flex justify-between"><span className="text-white/80">{t("loan.total_repayment")}</span><span className="font-semibold">{fmtCurrency(loan.total_repayment)}</span></div>
                </div>
            </div>
            <div className="p-6">
                <Button onClick={onDone} className="w-full h-12 rounded-xl bg-white font-semibold text-[#0A1930] hover:bg-slate-100" data-testid="approved-done-btn">
                    {t("loan.done")}
                </Button>
            </div>
        </div>
    );
}
